import { useQueryClient } from '@tanstack/react-query';
import { useLingui } from '@lingui/react/macro';
import { useEffect } from 'react';

import { useCallStore } from '#features/calls/model/callStore';
import { toaster } from '#features/ui/components/Toaster';
import { leaveVoice } from '#features/voice/model/voice';
import { useVoiceStore } from '#features/voice/model/voiceStore';

/**
 * Микрофон один, разговоров у человека может быть два — личный звонок и голос игровой сессии.
 * Здесь решается, кому он достанется.
 *
 * Правило взято у Discord и Telegram, и оно у обоих одно: **живое голосовое соединение ровно
 * одно**. Вход во второе выводит из первого. Без этого правила браузер честно отдаёт микрофон
 * обоим комнатам сразу, и человек, ответивший на личный звонок посреди сессии, транслирует этот
 * разговор всей команде — то есть это не про удобство, а про приватность.
 *
 * Звонок забирает микрофон не в момент звонка, а в момент, когда разговор действительно
 * начинается: исходящий (я сам набрал) и активный (я ответил). Входящий звонок сам по себе из
 * голоса не выводит — пока человек не нажал «ответить», он остаётся в разговоре с командой, и
 * отбирать у него микрофон из-за чужого гудка неправильно.
 *
 * Обратно в комнату никто не возвращается: у Discord после звонка тоже нужно зайти заново, и
 * молча вернуть человека в разговор, о котором он уже забыл, — худший из вариантов.
 */
export const useVoiceCallArbitration = () => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const callPhase = useCallStore((s) => s.phase);
  const inVoiceRoom = useVoiceStore((s) => s.phase !== 'idle');

  useEffect(() => {
    const callTakesTheMic = callPhase === 'outgoing' || callPhase === 'active';
    if (!callTakesTheMic || !inVoiceRoom) {
      return;
    }

    void leaveVoice(queryClient);
    toaster.create({
      title: t`post:voice.leftForCall`,
      description: t`post:voice.leftForCallDetail`,
      type: 'info',
      duration: 6000,
    });
  }, [callPhase, inVoiceRoom, queryClient, t]);
};

/** Мы сейчас в одной голосовой комнате с этим человеком? Тогда звонить ему незачем. */
export const useIsInVoiceWith = (peerUserId: string | null | undefined) =>
  useVoiceStore(
    (state) =>
      Boolean(peerUserId) &&
      state.phase !== 'idle' &&
      state.members.some((member) => member.userId === peerUserId && member.connected)
  );
